import { Button, Tabs, Skeleton } from '@mantine/core';
import { IconAlertCircle, IconArrowBackUp, IconArrowCapsule } from '@tabler/icons-react';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { displayedTestIdAtom } from '~/atoms/atoms';
import RunTable from '~/components/tables/run/RunTable';
import { useQueryParam } from '~/hooks/nav/useQueryParam';
import { useSetAsTrunk } from '~/hooks/test/useSetAsTrunk';
import { api } from '~/trpc-extra/api';
import { HeadTitle } from '../common/HeadTitle';
import HttpError from '../common/HttpError';
import BrowserPanel from './BrowserPanel';
import EditorPanel from './EditorPanel/EditorPanel';
import EditorPanelSkeleton from './EditorPanel/EditorPanelSkeleton';
import TestHeader from './TestHeader';
import TestSettings from './TestSettings';
import TestDraftTable from './steps/TestDraftTabble';

const DEFAULT_TAB = 'details';

const TestViewSkeleton = () => (
  <div className="px-4">
    <div className="py-6">
      <Skeleton
        height={50}
        radius="md"
        mb="lg"
      />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_1.2fr] xl:grid-cols-[1fr_2.3fr]">
        <div>
          <Skeleton
            height={40}
            radius="md"
            mb="md"
          />
          <Skeleton
            height={120}
            radius="md"
            mb="md"
          />
          <Skeleton
            height={200}
            radius="md"
            mb="md"
          />
          <Skeleton
            height={150}
            radius="md"
            mb="md"
          />
        </div>
        <div>
          <Skeleton
            height={40}
            radius="md"
            mb="md"
          />
          <Skeleton
            height={300}
            radius="md"
            mb="md"
          />
          <Skeleton
            height={200}
            radius="md"
            mb="md"
          />
        </div>
      </div>
    </div>
  </div>
);

// testId is more like the defaultID.
export default function TestView({
  testId,
  initialTab,
  isInDrawer = false,
}: {
  testId: string;
  initialTab?: string;
  isInDrawer?: boolean;
}) {
  const [displayedTestId, setDisplayedTestId] = useAtom(displayedTestIdAtom);

  const {
    data: testMeta,
    isLoading,
    error,
    isError,
  } = api.test.getMeta.useQuery({ id: displayedTestId || '' }, { enabled: !!displayedTestId });

  const [selectedTestContentVersion, setSelectedTestContentVersion] = useQueryParam('version');
  const slicedVersionId = selectedTestContentVersion?.slice(0, 5);
  const { data: testContent, isLoading: isLoadingTestContent } = api.testContent.get.useQuery(
    { id: selectedTestContentVersion || testMeta?.trunkContentId || '' },
    { refetchOnWindowFocus: false, enabled: !!selectedTestContentVersion || !!testMeta?.trunkContentId },
  );

  // Use URL params only when not in drawer, otherwise use local state
  const [queryParamTab, setQueryParamTab] = useQueryParam('tab');
  const [localTab, setLocalTab] = useState(initialTab || DEFAULT_TAB);

  // When in drawer use local state, when not use URL query parameters
  const activeTab = isInDrawer ? localTab : queryParamTab || DEFAULT_TAB;
  const setActiveTab = isInDrawer ? setLocalTab : setQueryParamTab;

  useEffect(() => {
    if (testId) {
      setDisplayedTestId(testId);
    }
  }, [setDisplayedTestId, testId]);

  const setAsTrunk = useSetAsTrunk();

  // We show skeleton until everything is loaded
  if (isLoading || isLoadingTestContent) {
    return <TestViewSkeleton />;
  }

  if (isError) {
    return (
      <div className="grid h-[80vh] place-items-center">
        <HttpError
          errorCode={error?.data?.code}
          httpStatus={error?.data?.httpStatus}
        />
      </div>
    );
  }

  return (
    <>
      <HeadTitle title={testMeta?.name || ''} />
      <div className={`flex h-full flex-col ${!isInDrawer ? 'p-4' : ''}`}>
        <div className="z-30">
          {!!testMeta && (
            <TestHeader
              testId={testId}
              isInDrawer={isInDrawer}
              testContentId={testMeta.trunkContentId || ''}
              selectedTestContentVersion={selectedTestContentVersion}
              setSelectedTestContentVersion={setSelectedTestContentVersion}
            />
          )}
          {/* Show a banner if the user is viewing a past version of the test */}
          {!!selectedTestContentVersion && (
            <div className="mx-4 flex items-center justify-center gap-6 rounded-md bg-orange-200 p-1 text-sm text-orange-900">
              <div className="flex items-center gap-2">
                <IconAlertCircle
                  className="mt-0.5"
                  size={16}
                />
                <div>
                  You are viewing an older version of this test.{' '}
                  <span className="font-semibold">(Version #{slicedVersionId})</span>
                </div>
              </div>
              <Button
                variant="light"
                size="compact-sm"
                color="orange"
                onClick={() => {
                  setSelectedTestContentVersion('');
                }}
                className="text-orange-900 hover:text-orange-800"
                leftSection={<IconArrowBackUp size={16} />}
              >
                Back to current version
              </Button>
              <Button
                variant="light"
                size="compact-sm"
                color="orange"
                className="text-orange-900 hover:text-orange-800"
                leftSection={<IconArrowCapsule size={16} />}
                onClick={() => setAsTrunk({ testContentId: selectedTestContentVersion })}
              >
                Revert to this version (#{slicedVersionId})
              </Button>
            </div>
          )}
        </div>
        <div className="flex-1 overflow-hidden">
          <Tabs
            value={activeTab}
            onChange={(value) => setActiveTab(value || DEFAULT_TAB)}
            classNames={{
              tab: '[&:not([data-active])]:opacity-60 [&:not([data-active])]:hover:opacity-100 px-2 mx-[5px]',
            }}
            className="mt-2 flex h-full flex-col px-4"
            keepMounted={false}
          >
            <Tabs.List>
              <Tabs.Tab value="details">Test Steps</Tabs.Tab>
              <Tabs.Tab value="editingDrafts">Editing Drafts</Tabs.Tab>
              <Tabs.Tab value="runs">Run History</Tabs.Tab>
              <Tabs.Tab value="settings">Settings</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel
              value="details"
              className="flex-1 overflow-hidden"
            >
              <div className="relative h-full w-full overflow-hidden">
                {/* We don't use split pane for test view. Only use it for test draft view because we have more panels there
                  and want users to be able to arrange them. */}
                <div className="grid h-full grid-cols-1 gap-2 md:grid-cols-[1fr_1.2fr] xl:grid-cols-[1fr_2.3fr]">
                  <div className="h-full overflow-auto">
                    {isLoadingTestContent || !testMeta ?
                      <EditorPanelSkeleton />
                    : testContent && (
                        <EditorPanel
                          testContentId={testContent.id}
                          readonly={true}
                          selectedTestContentVersion={selectedTestContentVersion}
                          hideControlBar
                        />
                      )
                    }
                  </div>
                  <div className="h-full overflow-auto">
                    {!!testContent && (
                      <BrowserPanel
                        className="h-full overflow-auto py-6"
                        testContentId={testMeta?.trunkContentId || ''}
                        testType={testContent.test.testType}
                      />
                    )}
                  </div>
                </div>
              </div>
            </Tabs.Panel>
            <Tabs.Panel
              value="editingDrafts"
              className="flex-1 overflow-auto"
            >
              <div className="px-8 py-8">
                <div className="font-medium">Editing Drafts</div>

                <div className="mt-2 w-[80%]">
                  <TestDraftTable testId={displayedTestId || ''} />
                </div>
              </div>
            </Tabs.Panel>
            <Tabs.Panel
              value="runs"
              className="flex-1 overflow-auto"
            >
              <div className="px-8 py-8">
                <div className="pb-8 font-medium">Past Runs</div>
                <RunTable testId={displayedTestId || ''} />
              </div>
            </Tabs.Panel>
            <Tabs.Panel
              value="settings"
              className="flex-1 overflow-auto"
            >
              <TestSettings testId={testId} />
            </Tabs.Panel>
          </Tabs>
        </div>
      </div>
    </>
  );
}
